import { useQuery } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";

import type { CrmDataProvider } from "../providers/supabase/dataProvider";

/**
 * The URL to show or open for a note attachment. Stored attachments live in
 * a private bucket (review #3), so their saved URL doesn't open on its own:
 * this swaps in a short-lived signed link for anything with a storage path.
 * Files not yet uploaded (blob:/data: previews) and links to other sites use
 * their src as is.
 *
 * @returns `undefined` while the signed link loads.
 */
export const useAttachmentUrl = (attachment?: {
  path?: string;
  src?: string;
}): string | undefined => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const path = attachment?.path;
  const src = attachment?.src;
  const isLocalPreview = !!src && (src.startsWith("blob:") || src.startsWith("data:"));
  const needsSigning = !!path && !isLocalPreview;
  const { data, isPending } = useQuery({
    queryKey: ["attachmentUrl", path],
    queryFn: () => dataProvider.getAttachmentUrl(path!),
    enabled: needsSigning,
    // Signed for an hour; refresh well before that.
    staleTime: 45 * 60 * 1000,
  });
  if (!needsSigning) return src;
  if (isPending) return undefined;
  return data ?? src;
};
